import React from "react";
import { useTranslation } from "react-i18next";
import { DoubleMaterialityESRSAreaEnum } from "@green0meter/api-client";

import { HEADER_VALUE } from "../AssignStakeholders.constants";
import { useESRSLabels } from "../../Stakeholders/components/useESRSLabels";

import { Box, Button, Checkbox, Typography } from "@components/base";
import Table, { TableColumn } from "@components/common/Table";
import IconAssignUsers from "@icons/AssignUsers";
import type { QuestionItemType } from "@api";

type StringSet = Set<string>;

type Row = { id: string; value: string; label: string; count: number };

interface TopicsSelectionProps {
  allQuestions: QuestionItemType[];
  esrsOptions: DoubleMaterialityESRSAreaEnum[];
  selectedEsrs: StringSet;
  assessmentTopicMap: Map<string, DoubleMaterialityESRSAreaEnum | null | undefined>;
  onToggle: (value: string) => void;
  onToggleAll: () => void;
  isAllSelected: boolean;
  onAssign: (level: "topic", label: string) => void;
  sectionRef?: React.RefObject<HTMLDivElement>;
  selectedStakeholderType: string;
}

const TopicsSelection: React.FC<TopicsSelectionProps> = ({
  allQuestions,
  esrsOptions,
  selectedEsrs,
  assessmentTopicMap,
  onToggle,
  onToggleAll,
  isAllSelected,
  onAssign,
  sectionRef,
  selectedStakeholderType,
}) => {
  const { t } = useTranslation();
  const { getEsrsLabelFromEnum } = useESRSLabels();

  const rows: Row[] = esrsOptions
    .map((code) => ({
      id: String(code),
      value: String(code),
      label: getEsrsLabelFromEnum(code),
      count: allQuestions.filter((q) => {
        const topic = assessmentTopicMap.get(q.assessmentId);

        return topic === code;
      }).length,
    }))
    .filter((row) => row.count > 0);

  const columns: TableColumn<Row>[] = [
    {
      title: t("common.select", "Select"),
      key: "select",
      renderCellContent: (row) => (
        <Checkbox
          checked={
            row.value === HEADER_VALUE ? isAllSelected : selectedEsrs.has(row.value)
          }
          onChange={() =>
            row.value === HEADER_VALUE ? onToggleAll() : onToggle(row.value)
          }
        />
      ),
      tableCellProps: { align: "center", sx: { width: 80 } },
    },
    {
      title: t("common.name", "Name"),
      key: "label",
      renderCellContent: (row) => (
        <Typography variant={row.value === HEADER_VALUE ? "subtitle2" : "body2"}>
          {row.label}
        </Typography>
      ),
    },
    {
      title: t("assignTopic.button", "Assign topic"),
      key: "assign",
      renderCellContent: (row) => (
        <Button
          variant="text"
          size="small"
          startIcon={<IconAssignUsers />}
          onClick={(e) => {
            e.stopPropagation();
            const label =
              row.value === HEADER_VALUE ? t("common.all", "All") : row.label;

            if (row.value === HEADER_VALUE) {
              if (!isAllSelected) {
                onToggleAll();
              }
            } else if (!selectedEsrs.has(row.value)) {
              onToggle(row.value);
            }

            onAssign("topic", label);
          }}
          disabled={!selectedStakeholderType}
        >
          {t("assignTopic.button", "Assign topic")}
        </Button>
      ),
      tableCellProps: { align: "right", sx: { width: 180 } },
    },
  ];

  return (
    <Box ref={sectionRef} sx={{ marginLeft: "32px !important" }}>
      <Table
        columns={columns}
        data={[
          {
            id: "",
            value: HEADER_VALUE,
            label: t("stakeholders.table.esrsArea", "Topic"),
            count: 0,
          },
          ...rows,
        ]}
        tableProps={{ size: "small", sx: { "& thead": { display: "none" } } }}
        onRowClick={(row) =>
          row.value === HEADER_VALUE ? onToggleAll() : onToggle(row.value)
        }
      />
    </Box>
  );
};

export default TopicsSelection;
